import React, { useRef, useEffect, useState, useCallback } from "react";

interface ResizablePanelProps {
  children: React.ReactNode;
  initialWidth: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
  className?: string;
  resizeHandleClassName?: string;
  resizeDirection?: "left" | "right";
}

const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  initialWidth,
  minWidth = 200,
  maxWidth = 800,
  onWidthChange,
  className = "",
  resizeHandleClassName = "",
  resizeDirection = "right",
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [width]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - startXRef.current;
      let newWidth;

      if (resizeDirection === "right") {
        // For right resize (CommunicationList), increase width when dragging right
        newWidth = startWidthRef.current + deltaX;
      } else {
        // For left resize (CommunicationDetailsPanel), decrease width when dragging left
        newWidth = startWidthRef.current - deltaX;
      }

      newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [isResizing, minWidth, maxWidth, onWidthChange, resizeDirection]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    setWidth(initialWidth);
  }, [initialWidth]);

  return (
    <div
      ref={panelRef}
      className={`relative ${className}`}
      style={{ width: `${width}px` }}
    >
      {children}
      <div
        className={`absolute top-0 w-1 h-full cursor-col-resize hover:bg-verba-blue dark:hover:bg-verba-blue-dk transition-colors ${resizeHandleClassName}`}
        onMouseDown={handleMouseDown}
        style={{
          [resizeDirection === "right" ? "right" : "left"]: "-2px",
          width: "4px",
        }}
      />
    </div>
  );
};

export default ResizablePanel;
